'use client'

import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useParams } from 'next/navigation'
import {
  MediaControlBar,
  MediaSeekBackwardButton,
  MediaPlayButton,
  MediaSeekForwardButton,
  MediaTimeRange,
  MediaTimeDisplay,
  MediaMuteButton,
  MediaVolumeRange,
  MediaPipButton,
  MediaFullscreenButton,
  MediaCaptionsButton,
  MediaPreviewChapterDisplay,
  MediaPreviewThumbnail,
  MediaPreviewTimeDisplay,
  MediaCastButton
} from 'media-chrome/react'
import { MediaSettingsMenuButton } from 'media-chrome/react/menu'
import { getMediaControlBarIcon } from '@/app/constants/icons'
import { prepareVideoChapters, formatVideoSeconds } from '@/utils/extra'

export default function Control ({ isLandscapeVideo, ...props }) {
  const params = useParams()
  const containerRef = useRef(null)
  const [screenSize, setScreenSize] = useState('')
  const [showChapters, setShowChapters] = useState(false)
  // Toggle and dispatch open event
  const toggleChapters = () => {
    setShowChapters(prev => {
      const newState = !prev
      if (newState) {
        // Slight delay to ensure layout is painted
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('chapter-drawer-opened'))
        }, 50)
      }
      return newState
    })
  }

  const {
    rewind: _isRewind = false,
    play: _isPlay = false,
    fastForward: _isFastForward = false,
    progress: isProgress = false,
    currentTime: _isCurrentTime = false,
    duration: _isShowDuration = false,
    disableSeek: _isDisabledSeekbar = false,
    mute: _isMute = false,
    volume: _isVolume = false,
    captions: _isCaptions = false,
    settings: _isSettings = false,
    pip: _isPip = false,
    fullscreen: _isFullscreen = false,
    poweredBy: _isPoweredBy = false,
    downloadButton: _isDownload = false,
    cast: _isCastAvailable = false
  } = props.controls || {}

  const _isLive = props?.isLive || false
  const _isDrmEnabled = props?.drmEnabled || false
  const isChapters = props?.chapters?.length > 0 || false
  const chapters = isChapters ? prepareVideoChapters(props?.chapters, props.poster) : []
  const convertTitle = props.language

  // Highlight chapter logic
  useEffect(() => {
    const onHighlight = (e) => {
      const currentTime = e.detail
      const chapterItems = containerRef.current?.querySelectorAll('.chapter-item') || []

      let closestItem = null
      let closestStart = -1

      chapterItems.forEach((item) => {
        const startTime = parseFloat(item.dataset.startTime)
        if (startTime <= currentTime && startTime > closestStart) {
          closestStart = startTime
          closestItem = item
        }
        item.classList.remove('highlited-chapters-items')
      })

      if (closestItem) {
        closestItem.classList.add('highlited-chapters-items')
      }
    }

    const onDrawerOpened = () => {
      const container = containerRef.current
      if (!container) return
      window.requestAnimationFrame(() => {
        const highlighted = container.querySelector('.chapter-item.highlited-chapters-items')
        if (!highlighted) return

        const offsetTop = highlighted.offsetTop
        const itemHeight = highlighted.offsetHeight
        const containerHeight = container.clientHeight

        const scrollTop = offsetTop - containerHeight / 2 + itemHeight / 2

        container.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        })
      })
    }

    window.addEventListener('highlight-chapter', onHighlight)
    window.addEventListener('chapter-drawer-opened', onDrawerOpened)

    return () => {
      window.removeEventListener('highlight-chapter', onHighlight)
      window.removeEventListener('chapter-drawer-opened', onDrawerOpened)
    }
  }, [])

  const chapterList = useMemo(() => {
    return chapters.map((chapter, index) => (
      <div
        key={index}
        className='chapter-item flex gap-2 px-1 border-b border-[#363535d9] last:border-b-0 items-center py-2 cursor-pointer'
        onClick={() => window.moveToChapter(chapter.startTime)}
        data-start-time={chapter.startTime}
      >
        {!_isDrmEnabled
          ? (
            <img
              src={chapter.thumbnail || 'fallback.jpg'}
              alt='Chapter thumbnail'
              className='h-12 w-20 rounded object-cover lg:block hidden'
            />)
          : <></>}
        <div
          className={
            `text-sm text-white flex gap-1 justify-between w-full
            ${!_isDrmEnabled ? 'lg:flex-col flex-row' : 'flex-row'}`
          }
        >
          <div className='lg:font-medium text-[0.7rem] lg:text-xs line-clamp-1 truncate lg:order-none order-1 '>{chapter.label}</div>
          <div className='text-[0.7rem]'>
            <span className='bg-zinc-600/90 py-0.5 px-1 rounded'>{formatVideoSeconds(chapter.startTime)}</span>
          </div>
        </div>
      </div>
    ))
  }, [chapters])

  useEffect(() => {
    const size = window.innerWidth
    setScreenSize(size > 600 ? 'md' : 'sm')
  }, [])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.chapter-menu-drawer') && !e.target.closest('.media-download-button')) {
        setShowChapters(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (showChapters) {
      const mediaController = document.querySelector('media-controller')
      if (mediaController) {
        mediaController.setAttribute('autohide', '-1')
      }
    }
  }, [showChapters])

  return (
    <>
      <div className='gumlet-gradient-bottom' />

      {!_isDisabledSeekbar && isProgress && (
        <MediaTimeRange>
          <MediaPreviewThumbnail />
          <MediaPreviewChapterDisplay />
          <MediaPreviewTimeDisplay />
        </MediaTimeRange>
      )}

      <MediaControlBar>
        {_isRewind && (
          <MediaSeekBackwardButton seekOffset={10} className='gum-icon'>
            <span slot='icon'>
              {getMediaControlBarIcon('MediaSeekBackwardButton', convertTitle.rewind)}
            </span>
          </MediaSeekBackwardButton>
        )}

        {_isPlay && (
          <MediaPlayButton className='gum-icon'>
            <span slot='play'>
              {getMediaControlBarIcon('MediaPlayButton', convertTitle.play)}
            </span>
            <span slot='pause'>
              {getMediaControlBarIcon('MediaPauseButton', convertTitle.pause)}
            </span>
          </MediaPlayButton>
        )}

        {_isFastForward && (
          <MediaSeekForwardButton seekOffset={10} className='gum-icon'>
            <span slot='icon'>
              {getMediaControlBarIcon('MediaSeekForwardButton', convertTitle.fastForward)}
            </span>
          </MediaSeekForwardButton>
        )}

        {_isMute && _isVolume && (
          <MediaMuteButton className='gum-icon'>
            <span slot='high'>{getMediaControlBarIcon('MediaVolumeButtonHigh', convertTitle.volume)}</span>
            <span slot='low'>{getMediaControlBarIcon('MediaVolumeButtonLow', convertTitle.volume)}</span>
            <span slot='medium'>{getMediaControlBarIcon('MediaVolumeButtonLow', convertTitle.volume)}</span>
            <span slot='off'>{getMediaControlBarIcon('MediaVolumeButtonMute', convertTitle.mute)}</span>
          </MediaMuteButton>
        )}

        {_isVolume && <MediaVolumeRange />}

        {(_isCurrentTime || _isShowDuration) && (
          <>
            <MediaTimeDisplay />
            <MediaTimeDisplay showduration />
          </>
        )}

        <span className='control-spacer' />

        {_isCastAvailable && <MediaCastButton id='castBtn' />}

        {_isLive && (
          <div className='flex items-center'>
            <span className='text-white text-xs'>
              <div className='bg-red-600 px-2 py-1 rounded flex items-center gap-1 live-button'>
                <div className='w-1.5 h-1.5 bg-white rounded-full' />
                Live
              </div>
            </span>
          </div>
        )}

        {isChapters && (
          <div
            className='gum-icon flex items-center media-download-button justify-center cursor-pointer relative isolate group'
            onClick={toggleChapters}
          >
            {/* Tooltip */}
            {!showChapters &&
              (
                <div className='absolute bottom-full mb-3.5 w-max px-2.5 py-1 text-xs text-white bg-[#14141e] rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10'>
                  Chapters
                </div>
              )}
            <span slot='icon' title={convertTitle.chapters}>
              {getMediaControlBarIcon('MediaChaptersButton', convertTitle.chapters)}
            </span>
            <div
              ref={containerRef}
              className={`media-settings-menu-style chapter-menu-drawer w-100 max-h-[140px] md:max-h-[200px] overflow-y-auto lg:w-[280px] lg:max-w-[500px] lg:max-h-[400px] w-[200px] lg:bottom-[61px] bottom-[48px]  ${
                showChapters ? 'block' : 'hidden'
              }`}
            >
              {chapterList}
            </div>
          </div>
        )}

        {_isDownload && props.downloadUrl && (
          <div className='gum-icon flex items-center media-download-button justify-center cursor-pointer'>
            <a className='gum-download-button' href={props.downloadUrl} title={convertTitle.download}>
              <span slot='icon'>
                {getMediaControlBarIcon('MediaDownloadButton', convertTitle.download)}
              </span>
            </a>
          </div>
        )}

        {_isCaptions && (
          <MediaCaptionsButton className='gum-icon'>
            <span slot='on'>{getMediaControlBarIcon('MediaEnableCaptionsButton', convertTitle.enableCaption)}</span>
            <span slot='off'>{getMediaControlBarIcon('MediaDisableCaptionsButton', convertTitle.disableCaption)}</span>
          </MediaCaptionsButton>
        )}

        {_isSettings && (
          <MediaSettingsMenuButton className='gum-icon'>
            <span slot='icon'>{getMediaControlBarIcon('MediaSettingsMenuButton', convertTitle.settings)}</span>
          </MediaSettingsMenuButton>
        )}

        {screenSize === 'md' && isLandscapeVideo && _isPip && (
          <MediaPipButton className='gum-icon'>
            <span slot='enter'>{getMediaControlBarIcon('MediaPipButton', convertTitle.pip)}</span>
          </MediaPipButton>
        )}

        {_isFullscreen && (
          <MediaFullscreenButton className='gum-icon'>
            <span slot='enter'>{getMediaControlBarIcon('MediaFullscreenButton', convertTitle.enterFullscreen)}</span>
            <span slot='exit'>{getMediaControlBarIcon('MediaExitFullscreenButton', convertTitle.exitFullscreen)}</span>
          </MediaFullscreenButton>
        )}

        {_isPoweredBy && (
          <div id='media-powered-by' className='flex items-center'>
            <a
              href={`https://www.gumlet.com/video-hosting/?utm_source=${props?.referer}&utm_medium=play.gumlet.io&utm_content=${params?.assetId}&utm_campaign=embed-control-logo`}
              target='_blank'
              title='Powered by Gumlet'
              rel='noreferrer'
            >
              <img
                loading='lazy'
                src='https://assets.gumlet.io/assets/gumlet-text-logo.svg'
                className='w-auto h-[12px] hidden md:block'
                alt='Powered by Gumlet'
              />
              <img
                loading='lazy'
                src='https://assets.gumlet.io/assets/white-icon.svg'
                className='w-auto h-[28px] p-[6px] md:hidden'
                alt='Powered by Gumlet'
              />
            </a>
          </div>
        )}
      </MediaControlBar>
    </>
  )
}
